/**
 * Is this browser a registered store tablet, and if so, which store?
 *
 * A tablet IT has enrolled carries its own long-lived device cookie, so it is
 * inside The Sweet Spot with nobody signed in and no password typed. This hook
 * asks the API on load and does two things at once:
 *
 *   1. Tells the app it is running on a store tablet, which is what turns on the
 *      store-tablet strip on the home screen.
 *   2. Refreshes the device cookie when it is more than a day old. That refresh
 *      is why an enrolled tablet never drops to the sign-in screen: the API
 *      re-issues the cookie on this call rather than on every request.
 *
 * A 401 here is the normal answer for everyone else (an ordinary signed-in
 * person on a laptop or a phone), so it is not an error state and nothing is
 * shown. The call runs once per app load and is not retried.
 */

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';
import { STORES_API_BASE } from './useStores';

export type KioskDevice = {
  id: number;
  store_no: string;
  store_name: string | null;
  label: string | null;
  status: string;
  store_account: string | null;
  last_seen_at: string | null;
};

export type KioskSession = {
  device: KioskDevice;
  /** The person who tapped their employee id on this tablet, when someone has. */
  actor: { employee_id: string; name: string } | null;
};

export function useKioskSession() {
  return useQuery<KioskSession, Error>({
    queryKey: ['kiosk', 'session'],
    queryFn: () => apiGet<KioskSession>(`${STORES_API_BASE}/kiosk/session`),
    // Once per app load. The cookie refresh is a side effect of the call, so
    // re-asking on every focus would mint cookies for no reason.
    staleTime: Infinity,
    retry: false,
    // A 401 is the expected answer on every browser that is not an enrolled
    // tablet, so it must not surface as a broken query anywhere.
    throwOnError: false,
  });
}

/** True when this browser is an enrolled, active store tablet. */
export function useIsKiosk(): boolean {
  const q = useKioskSession();
  return q.data?.device.status === 'active';
}
